import React, { useState, useEffect, useCallback } from 'react';
import styles from './ConsultantsList.module.css';
import { GET, PUT, POST, deleteRequest } from '../../../../config/api';

const emptyForm = {
  user: '',
  title: '',
  profession: '',
  specialty: '',
  pricePerSession: '',
  about: '',
  education: '',
  university: '',
  experience: '',
  approved: false,
  featured: '0',
  deleted: false,
};

const ConsultantsList = () => {
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [addForm, setAddForm] = useState(emptyForm);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchConsultants = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, message } = await GET('/website/admin/consultants');
    if (data?.consultants) {
      setConsultants(data.consultants);
    } else {
      setError(message || 'Failed to load consultants');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchConsultants();
  }, [fetchConsultants]);

  const buildPayload = (formData) => {
    const payload = {
      title: formData.title.trim(),
      profession: formData.profession.trim(),
      specialty: formData.specialty.trim(),
      about: formData.about.trim(),
      pricePerSession: Number(formData.pricePerSession),
      approved: formData.approved,
      featured: Number(formData.featured),
      deleted: formData.deleted,
    };

    if (formData.user?.trim()) {
      payload.user = formData.user.trim();
    }
    if (formData.education.trim()) payload.education = formData.education.trim();
    if (formData.university.trim()) payload.university = formData.university.trim();
    if (formData.experience.trim()) payload.experience = formData.experience.trim();

    return payload;
  };

  const consultantToForm = (consultant) => ({
    user: consultant.user?._id || consultant.user || '',
    title: consultant.title || '',
    profession: consultant.profession || '',
    specialty: consultant.specialty || '',
    pricePerSession: String(consultant.pricePerSession ?? ''),
    about: consultant.about || '',
    education: consultant.education || '',
    university: consultant.university || '',
    experience: consultant.experience || '',
    approved: !!consultant.approved,
    featured: String(consultant.featured ?? 0),
    deleted: !!consultant.deleted,
  });

  const startAdd = () => {
    setShowAddForm(true);
    setEditingId(null);
    setAddForm(emptyForm);
    setSaveMessage(null);
  };

  const cancelAdd = () => {
    setShowAddForm(false);
    setAddForm(emptyForm);
  };

  const handleCreate = async () => {
    setSaving(true);
    setSaveMessage(null);
    const { data, message } = await POST('/website/admin/consultants', {}, buildPayload(addForm));

    if (data?.consultant) {
      setConsultants((prev) => [data.consultant, ...prev]);
      setSaveMessage({ type: 'success', text: 'Consultant created.' });
      setShowAddForm(false);
      setAddForm(emptyForm);
    } else {
      setSaveMessage({ type: 'error', text: message || 'Failed to create consultant.' });
    }
    setSaving(false);
  };

  const startEdit = (consultant) => {
    setShowAddForm(false);
    setEditingId(consultant._id);
    setForm(consultantToForm(consultant));
    setSaveMessage(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSave = async (consultantId) => {
    setSaving(true);
    setSaveMessage(null);
    const { data, message } = await PUT(`/website/admin/consultants/${consultantId}`, {}, buildPayload(form));

    if (data?.consultant) {
      setConsultants((prev) => prev.map((c) => (c._id === consultantId ? data.consultant : c)));
      setSaveMessage({ type: 'success', text: 'Consultant updated.' });
      setEditingId(null);
      setForm(emptyForm);
    } else {
      setSaveMessage({ type: 'error', text: message || 'Failed to update consultant.' });
    }
    setSaving(false);
  };

  const handleDelete = async (consultant) => {
    if (!window.confirm(`Delete consultant "${consultant.title}"? This marks the profile as deleted.`)) {
      return;
    }
    setSaving(true);
    setSaveMessage(null);
    const { data, message, error: deleteError } = await deleteRequest({
      url: `/website/admin/consultants/${consultant._id}`,
    });

    if (data?.consultant) {
      setConsultants((prev) => prev.map((c) => (c._id === consultant._id ? data.consultant : c)));
      setSaveMessage({ type: 'success', text: 'Consultant deleted.' });
      setEditingId(null);
    } else {
      setSaveMessage({ type: 'error', text: message || 'Failed to delete consultant.' });
    }
    setSaving(false);
  };

  const isFormValid = (formData) =>
    formData.user.trim() &&
    formData.title.trim() &&
    formData.profession.trim() &&
    formData.specialty.trim() &&
    formData.about.trim() &&
    formData.pricePerSession !== '' &&
    !Number.isNaN(Number(formData.pricePerSession));

  const filteredConsultants = consultants.filter((consultant) => {
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      const haystack = [
        consultant.title,
        consultant.profession,
        consultant.specialty,
        consultant.user?.username,
        consultant.user?.email,
        consultant.user?.firstName,
        consultant.user?.lastName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(term)) return false;
    }
    if (statusFilter === 'active') return !consultant.deleted;
    if (statusFilter === 'deleted') return consultant.deleted;
    if (statusFilter === 'approved') return consultant.approved && !consultant.deleted;
    if (statusFilter === 'pending') return !consultant.approved && !consultant.deleted;
    return true;
  });

  const renderForm = (formData, setFormData, onSubmit, submitLabel, onCancel, isCreate = false) => (
    <div className={styles['edit-form']}>
      <label>
        User ID <span className={styles.required}>*</span>
        <input
          type="text"
          value={formData.user}
          onChange={(e) => setFormData({ ...formData, user: e.target.value })}
          placeholder="MongoDB user _id"
        />
      </label>
      <label>
        Title <span className={styles.required}>*</span>
        <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
      </label>
      <label>
        Profession <span className={styles.required}>*</span>
        <input type="text" value={formData.profession} onChange={(e) => setFormData({ ...formData, profession: e.target.value })} />
      </label>
      <label>
        Specialty <span className={styles.required}>*</span>
        <input type="text" value={formData.specialty} onChange={(e) => setFormData({ ...formData, specialty: e.target.value })} />
      </label>
      <label>
        Price per session <span className={styles.required}>*</span>
        <input
          type="number"
          min="5"
          max="200"
          value={formData.pricePerSession}
          onChange={(e) => setFormData({ ...formData, pricePerSession: e.target.value })}
        />
      </label>
      <label>
        About <span className={styles.required}>*</span>
        <textarea rows={4} value={formData.about} onChange={(e) => setFormData({ ...formData, about: e.target.value })} />
      </label>
      <label>
        Education
        <input type="text" value={formData.education} onChange={(e) => setFormData({ ...formData, education: e.target.value })} />
      </label>
      <label>
        University
        <input type="text" value={formData.university} onChange={(e) => setFormData({ ...formData, university: e.target.value })} />
      </label>
      <label>
        Experience
        <input type="text" value={formData.experience} onChange={(e) => setFormData({ ...formData, experience: e.target.value })} />
      </label>
      <label>
        Featured
        <select value={formData.featured} onChange={(e) => setFormData({ ...formData, featured: e.target.value })}>
          <option value="0">Not featured</option>
          <option value="1">Featured</option>
          <option value="2">Featured & important</option>
        </select>
      </label>
      <label className={styles.checkbox}>
        <input type="checkbox" checked={formData.approved} onChange={(e) => setFormData({ ...formData, approved: e.target.checked })} />
        Approved
      </label>
      {!isCreate && (
        <label className={styles.checkbox}>
          <input type="checkbox" checked={formData.deleted} onChange={(e) => setFormData({ ...formData, deleted: e.target.checked })} />
          Deleted
        </label>
      )}
      <div className={styles['form-actions']}>
        <button className={styles['save-btn']} onClick={onSubmit} disabled={saving || !isFormValid(formData)}>
          {saving ? 'Saving...' : submitLabel}
        </button>
        <button className={styles['cancel-btn']} onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <div className={styles['spinner-text']}>Loading consultants...</div>
      </div>
    );
  }

  if (error) {
    return <div className={styles.error}>Error: {error}</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.hero}>
        <div className={styles.brand}>
          <img src="/logo192.png" alt="Tellmiz logo" className={styles['brand-logo']} />
          <div>
            <p className={styles.eyebrow}>Tellmiz Admin</p>
            <h1>Consultants</h1>
            <p className={styles.subhead}>Manage consultation profiles.</p>
          </div>
        </div>
        <div className={styles['stat-row']}>
          <div className={styles['stat-card']}>
            <span>Total</span>
            <strong>{consultants.length}</strong>
          </div>
          <div className={styles['stat-card']}>
            <span>Active</span>
            <strong>{consultants.filter((c) => !c.deleted).length}</strong>
          </div>
          <div className={styles['stat-card']}>
            <span>Approved</span>
            <strong>{consultants.filter((c) => c.approved && !c.deleted).length}</strong>
          </div>
          <div className={styles['stat-card']}>
            <span>Pending</span>
            <strong>{consultants.filter((c) => !c.approved && !c.deleted).length}</strong>
          </div>
        </div>
      </div>

      {saveMessage && (
        <div className={`${styles.banner} ${styles[saveMessage.type]}`}>{saveMessage.text}</div>
      )}

      <div className={styles['filters-panel']}>
        <label className={styles.filter}>
          <span>Search</span>
          <input
            type="text"
            placeholder="Title, profession, user..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </label>
        <label className={styles.filter}>
          <span>Status</span>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending approval</option>
            <option value="deleted">Deleted</option>
          </select>
        </label>
      </div>

      <div className={styles.toolbar}>
        <button className={styles['add-btn']} onClick={startAdd} disabled={showAddForm || !!editingId}>
          Add consultant
        </button>
        <button className={styles['refresh-btn']} onClick={fetchConsultants} disabled={loading}>
          Refresh
        </button>
      </div>

      {showAddForm && (
        <div className={styles['add-panel']}>
          <h2 className={styles['add-panel-title']}>New consultant</h2>
          {renderForm(addForm, setAddForm, handleCreate, 'Create', cancelAdd, true)}
        </div>
      )}

      <div className={styles['consultants-grid']}>
        {filteredConsultants.map((consultant) => {
          const isEditing = editingId === consultant._id;
          const userLabel = consultant.user?.username
            ? `${consultant.user.username} (${consultant.user.email || 'no email'})`
            : consultant.user?._id || 'Unknown user';

          return (
            <div key={consultant._id} className={`${styles['consultant-card']} ${consultant.deleted ? styles.deleted : ''}`}>
              <div className={styles['consultant-header']}>
                <div>
                  <h2>{consultant.title}</h2>
                  <p className={styles.subtitle}>{consultant.profession} · {consultant.specialty}</p>
                  <p className={styles.userLine}>{userLabel}</p>
                </div>
                <div className={styles['pill-row']}>
                  {consultant.deleted ? (
                    <span className={`${styles.pill} ${styles['pill-warn']}`}>Deleted</span>
                  ) : consultant.approved ? (
                    <span className={`${styles.pill} ${styles['pill-ok']}`}>Approved</span>
                  ) : (
                    <span className={`${styles.pill} ${styles['pill-pending']}`}>Pending</span>
                  )}
                  {consultant.featured > 0 && (
                    <span className={`${styles.pill} ${styles['pill-featured']}`}>
                      Featured {consultant.featured === 2 ? '★★' : '★'}
                    </span>
                  )}
                </div>
              </div>

              {isEditing ? (
                renderForm(
                  form,
                  setForm,
                  () => handleSave(consultant._id),
                  'Save',
                  cancelEdit,
                )
              ) : (
                <div className={styles['consultant-meta']}>
                  <span><strong>Price:</strong> ${consultant.pricePerSession}</span>
                  <span><strong>Ratings:</strong> {consultant.ratings?.length ?? 0}</span>
                  <span><strong>Updated:</strong> {consultant.updatedAt ? new Date(consultant.updatedAt).toLocaleString() : '—'}</span>
                  <p className={styles.aboutPreview}>{consultant.about}</p>
                  <div className={styles['card-actions']}>
                    <button className={styles['edit-btn']} onClick={() => startEdit(consultant)} disabled={saving}>
                      Edit
                    </button>
                    {!consultant.deleted && (
                      <button className={styles['delete-btn']} onClick={() => handleDelete(consultant)} disabled={saving}>
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ConsultantsList;
